import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Mail, ArrowLeft, Check } from 'lucide-react';
import axios from 'axios';

const API_URL = '';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email, frontend_url: window.location.origin });
      setEmailSent(true);
      toast.success('Password reset email sent!');
    } catch (error) {
      // Always show success to prevent email enumeration
      setEmailSent(true);
      toast.success('If an account exists with this email, you will receive a reset link.');
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F7F4EF' }}>
        <div className="w-full max-w-md px-4">
          <Card className="shadow-xl border-0 text-center">
            <CardHeader className="pb-2">
              <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <CardTitle className="text-2xl font-bold text-stone-900">Check your email</CardTitle>
              <CardDescription className="text-stone-500">
                We've sent a password reset link to <strong>{email}</strong>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 pt-4">
              <p className="text-sm text-stone-500">
                Click the link in your email to reset your password. If you don't see it, check your spam folder.
              </p>
              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setEmailSent(false)}
                >
                  Try different email
                </Button>
                <Link to="/login">
                  <Button variant="ghost" className="w-full text-stone-600">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to login
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F7F4EF' }}>
      <div className="w-full max-w-md px-4">
        <Card className="shadow-xl border-0">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center mb-4">
              <Mail className="w-8 h-8 text-stone-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-stone-900">Forgot password?</CardTitle>
            <CardDescription className="text-stone-500">
              No worries, we'll send you reset instructions.
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-stone-700">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-11"
                  data-testid="forgot-password-email"
                />
              </div>
            </CardContent>
            <CardFooter className="flex flex-col space-y-4 pt-2">
              <Button
                type="submit"
                className="w-full h-11 bg-stone-900 hover:bg-stone-800 text-white rounded-lg"
                disabled={loading}
                data-testid="forgot-password-submit"
              >
                {loading ? 'Sending...' : 'Reset password'}
              </Button>
              <Link to="/login" className="w-full">
                <Button variant="ghost" className="w-full text-stone-600">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to login
                </Button>
              </Link>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
}
